import useCompanies from "src/api/hooks/useCompanies";
import { SettingsCard } from "./SettingsCard";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useMyUser from "src/api/hooks/useMyUser";
import { useQueryClient } from "@tanstack/react-query";
import { lang } from "src/language/Strings";
import { routes, useRoute } from "src/routes";
import { LocalStorageKeys } from "src/utils/localStorageKeys";
import { Modal } from "../Modal";
import { authManager } from "src/main";
import { usePosition } from "src/api/hooks/usePosition";
import { useCallback, useEffect } from "react";
import MultipleSelect from "../Select/MultipleSelect";

/**
 * Hook that returns a function to replace route parameters dynamically.
 * Uses the current route template from routes.tsx to ensure you stay on the same page
 * when changing parameters.
 *
 * @returns A function that takes a partial record of parameters to replace and returns the new path
 */
const useReplaceParams = () => {
  const currentRoute = useRoute();
  const currentParams = useParams();

  const location = useLocation();

  return useCallback(
    (newParams: Partial<Record<string, string | undefined>>) => {
      // If we have a route template, use it
      if (currentRoute?.path) {
        let path = currentRoute.path;
        // Merge current params with new params
        const mergedParams = { ...currentParams, ...newParams };
        // Replace all parameter placeholders with actual values
        path = path.replace(/:(\w+)/g, (match, paramName) => {
          return mergedParams[paramName] || match;
        });
        return path;
      }

      // Fallback: if we can't find the route template, try to replace params in current pathname
      // This is less reliable but works as a backup
      let path = location.pathname;
      const mergedParams = { ...currentParams, ...newParams };
      // Extract parameter names from current params and replace their values
      Object.entries(mergedParams).forEach(([key, value]) => {
        if (value) {
          // Find the current value in the path and replace it
          const currentValue = currentParams[key];
          if (currentValue) {
            path = path.replace(currentValue, value);
          }
        }
      });
      return path;
    },
    [currentRoute, currentParams, location.pathname]
  );
};

export default function SettingsMenu({
  isMenuOpen,
  setIsMenuOpen,
}: {
  isMenuOpen: boolean;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
}) {
  const companies = useCompanies();
  const navigate = useNavigate();
  const { data: myUser } = useMyUser();
  const queryClient = useQueryClient();
  const { companyId } = useParams();
  const location = useLocation();
  const position = usePosition();
  const replaceParams = useReplaceParams();

  useEffect(() => {
    const headerEl = document.getElementById("header-title");
    const originalTitle = headerEl?.innerHTML;
    if (!headerEl) return;
    if (isMenuOpen) {
      headerEl.innerHTML = lang.settings;
    } else {
      headerEl.innerHTML = originalTitle ?? "";
    }
    return () => {
      headerEl.innerHTML = originalTitle ?? "";
    };
  }, [isMenuOpen]);
  return isMenuOpen ? (
    <Modal
      onOutsideClick={() => setIsMenuOpen(false)}
      className="w-full h-full max-h-full"
    >
      {[1, 3, 5].includes(myUser?.roleId || 0) ? (
        <>
          <SettingsCard
            icon="business"
            setIsMenuOpen={setIsMenuOpen}
            className="w-full min-h-20"
          >
            {companies && companies.length > 1 ? (
              <select
                className="w-[80%] p-4 rounded-md bg-White border-2 border-Grey4"
                defaultValue={companyId}
                onChange={(e) => {
                  const newPath = replaceParams({ companyId: e.target.value });
                  navigate(newPath);
                  setIsMenuOpen(false);
                }}
              >
                {companies?.map((c) => (
                  <option key={`company-${c.id}`} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            ) : null}
          </SettingsCard>

          <SettingsCard
            icon="business"
            setIsMenuOpen={setIsMenuOpen}
            className="w-full min-h-20"
          >
            <div className="flex flex-row gap-2 items-start">
              <span className="text-PrimaryGreen font-medium text-base">
                {lang.selectedGroups}
              </span>
            </div>
            {myUser?.groups?.length !== 0 ? (
              <MultipleSelect 
                defaultValue={(() => {
                  try {
                    const stored = localStorage.getItem(LocalStorageKeys.selectedGroups);
                    const parsed = stored ? JSON.parse(stored) : [];
                    return Array.isArray(parsed) ? parsed : [];
                  } catch {
                    return [];
                  }
                })()} 
                options={myUser?.groups?.map((g) => ({label: g.name, value: g.id}))} 
                onChange={(e) => {
                  if (e.length !== 0) {
                    localStorage.setItem(LocalStorageKeys.groupId, e[0]);
                  }
                  localStorage.setItem(LocalStorageKeys.selectedGroups, JSON.stringify(e));
                  console.log(e)
                }}
              />
            ) : <small className="text-PrimaryRed">{lang.noUserGroups}</small>}
            
          </SettingsCard>
          {!(
            (position.latitude && position.longitude) ||
            position.latitude === 0 ||
            position.longitude === 0
          ) && (
            <SettingsCard
              onClick={() => {
                // Request permission to use location data
                navigator.geolocation.getCurrentPosition(
                  () => {},
                  (error) => {
                    console.error(error);
                  }
                );
              }}
              icon="location"
              setIsMenuOpen={setIsMenuOpen}
              className="w-full min-h-20"
            >
              <div>
                <p>{lang.activateLocationData}</p>
              </div>
            </SettingsCard>
          )}
          <SettingsCard
            icon="department"
            setIsMenuOpen={setIsMenuOpen}
            className="w-full min-h-20"
            onClick={() => {
              setIsMenuOpen(false);
              navigate(routes.Groups.manage.url(companyId!));
            }}
          >
            <div>
              <p>{lang.manageGroups}</p>
            </div>
          </SettingsCard>
          <SettingsCard
            icon="department"
            setIsMenuOpen={setIsMenuOpen}
            className="w-full min-h-20"
            onClick={() => {
              setIsMenuOpen(false);
              navigate(routes.Users.url(companyId!));
            }}
          >
            <div>
              <p>{lang.manageUsers}</p>
            </div>
          </SettingsCard>
        </>
      ) : (
        <SettingsCard
          icon="home"
          setIsMenuOpen={setIsMenuOpen}
          onClick={() => {
            setIsMenuOpen(false);
            if (
              location.pathname.startsWith(
                routes.Egenkontroller.Pages.List.url(myUser.companyId!)
              )
            ) {
              navigate(routes.Egenkontroller.Pages.List.url(myUser.companyId!));
            } else {
              navigate(routes.Deviation.Pages.List.url(myUser.companyId!));
            }
          }}
        >
          <div>
            <p>{lang.home}</p>
          </div>
        </SettingsCard>
      )}

      <SettingsCard
        icon="refresh"
        setIsMenuOpen={setIsMenuOpen}
        onClick={() => {
          setIsMenuOpen(false);
          queryClient.invalidateQueries();
        }}
      >
        <div>
          <p>{lang.refresh}</p>
        </div>
      </SettingsCard>
      {/* <SettingsCard
        icon="book"
        setIsMenuOpen={setIsMenuOpen}
        onClick={() => {
          setIsMenuOpen(false);
          queryClient.invalidateQueries();
          navigate(routes.Reports.url(companyId!));
        }}
      >
        <div>
          <p>{lang.reports}</p>
        </div>
      </SettingsCard> */}

      <SettingsCard
        icon="sign-out"
        setIsMenuOpen={setIsMenuOpen}
        onClick={() => {
          setIsMenuOpen(false);
          authManager.signOut();
          navigate("/login");
          localStorage.clear();
        }}
      >
        <div>
          <p>{lang.signOut}</p>
        </div>
      </SettingsCard>
    </Modal>
  ) : null;
}
